import { BookOpen, ChevronDown, ChevronUp, FileText, Printer } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

type StudentRecord = {
  id: string;
  name: string;
  period: string;
  overallGrade: string;
  comments?: string;
  subjects: Array<{ name: string; grade: string }>;
  monthlyProgress: Array<{
    month: string;
    overall: string;
    comments?: string;
    subjects: Array<{ name: string; grade: string }>;
  }>;
  reportCards: Array<{ id: string; title: string; date: string }>;
};

const STUDENTS = [
  {
    id: 'yael',
    name: 'Yael Gertler',
    period: 'Semester 1',
    overallGrade: 'A-',
    comments: 'Yael demonstrates strong consistency and a thoughtful approach to her work.',
    subjects: [
      { name: 'Chumash', grade: 'A' },
      { name: 'Math', grade: 'A-' },
      { name: 'Language Arts', grade: 'A' },
      { name: 'Science', grade: 'B+' },
      { name: 'Hebrew', grade: 'A-' },
      { name: 'Social Studies', grade: 'A' },
    ],
    monthlyProgress: [
      {
        month: 'January',
        overall: 'A-',
        comments: 'Continues to participate consistently and complete assignments on time.',
        subjects: [
          { name: 'Chumash', grade: 'A' },
          { name: 'Math', grade: 'A-' },
          { name: 'Language Arts', grade: 'A' },
        ],
      },
      {
        month: 'December',
        overall: 'B+',
        comments: 'Showed improvement in writing and independent work.',
        subjects: [
          { name: 'Chumash', grade: 'A-' },
          { name: 'Math', grade: 'B+' },
          { name: 'Language Arts', grade: 'B+' },
        ],
      },
    ],
    reportCards: [
      { id: 'yael-sem1', title: 'Semester 1 Report Card', date: 'Jan 29, 2026' },
      { id: 'yael-progress', title: 'Progress Report', date: 'Nov 14, 2025' },
    ],
  },
  {
    id: 'david',
    name: 'David Gertler',
    period: 'Semester 1',
    overallGrade: 'B+',
    comments: 'David is engaged and steadily improving across both Judaic and general studies.',
    subjects: [
      { name: 'Gemara', grade: 'B+' },
      { name: 'Math', grade: 'B' },
      { name: 'Language Arts', grade: 'A-' },
      { name: 'Science', grade: 'B+' },
      { name: 'Hebrew', grade: 'B+' },
      { name: 'Social Studies', grade: 'B+' },
    ],
    monthlyProgress: [
      {
        month: 'January',
        overall: 'B+',
        comments: 'Strong participation and better homework consistency.',
        subjects: [
          { name: 'Gemara', grade: 'B+' },
          { name: 'Math', grade: 'B' },
          { name: 'Language Arts', grade: 'A-' },
        ],
      },
    ],
    reportCards: [{ id: 'david-sem1', title: 'Semester 1 Report Card', date: 'Jan 29, 2026' }],
  },
];

const YEARS = ['2025-2026', '2024-2025'];

export function ParentAcademicPerformanceSourceFaithfulPage() {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [selectedYear, setSelectedYear] = useState('2025-2026');
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);

  const currentStudentId = studentId ?? STUDENTS[0].id;
  const currentStudent = useMemo(
    () => STUDENTS.find((student) => student.id === currentStudentId) ?? STUDENTS[0],
    [currentStudentId],
  );

  return (
    <div
      data-testid="parent-academic-source-faithful-root"
      data-extracted-from="source-faithful:parent-portal-academic-performance-v2.0"
      data-route-family="source-faithful"
      data-route-ready="true"
      className="source-faithful-shell"
      style={{ '--ystnj-source-faithful-max-width': '1120px' } as React.CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="source-faithful-header">
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <BookOpen size={20} className="text-primary" /> Academic Performance
          </h1>
          <div className="source-faithful-headerActions">
            <div className="w-full sm:w-48">
              <label className="sr-only" htmlFor="academic-student">Student</label>
              <select
                id="academic-student"
                value={currentStudent.id}
                onChange={(event) => navigate(`/parent/portal/academic/${event.target.value}`)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {STUDENTS.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-40">
              <label className="sr-only" htmlFor="academic-year">Academic year</label>
              <select
                id="academic-year"
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {YEARS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{currentStudent.period} Summary</h2>
              <span className="mt-1 inline-block rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
                {selectedYear}
              </span>
            </div>
            <div className="text-center">
              <span className="block text-3xl font-black text-primary">{currentStudent.overallGrade}</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Overall</span>
            </div>
          </div>

          <div className="source-faithful-kpiGrid mb-6">
            {currentStudent.subjects.map((subject) => (
              <div key={subject.name} className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                <span className="text-sm font-medium text-gray-700">{subject.name}</span>
                <span className="font-bold text-gray-900">{subject.grade}</span>
              </div>
            ))}
          </div>

          {currentStudent.comments ? (
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/50 p-4 text-sm italic text-gray-600">
              "{currentStudent.comments}"
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => window.print()}
              className="flex items-center text-sm font-bold text-primary hover:underline"
            >
              <Printer size={16} className="mr-2" /> Print Summary
            </button>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900">Monthly Progress</h2>
          {currentStudent.monthlyProgress.map((progress) => (
            <div key={progress.month} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <button
                type="button"
                className="flex w-full items-center justify-between p-4 transition-colors hover:bg-gray-50"
                onClick={() => setExpandedMonth((open) => (open === progress.month ? null : progress.month))}
              >
                <div className="flex items-center gap-4">
                  <span className="font-bold text-gray-700">{progress.month}</span>
                </div>
                <div className="flex items-center gap-6">
                  <span className={`font-bold ${progress.overall.startsWith('A') ? 'text-green-600' : 'text-primary'}`}>
                    {progress.overall}
                  </span>
                  {expandedMonth === progress.month ? (
                    <ChevronUp size={20} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400" />
                  )}
                </div>
              </button>
              {expandedMonth === progress.month ? (
                <div className="animate-in slide-in-from-top-2 space-y-4 border-t border-gray-100 bg-gray-50/50 p-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {progress.subjects.map((subject) => (
                      <div key={subject.name} className="flex justify-between rounded border border-gray-100 bg-white p-2 text-sm">
                        <span className="text-gray-600">{subject.name}</span>
                        <span className="font-bold text-gray-900">{subject.grade}</span>
                      </div>
                    ))}
                  </div>
                  {progress.comments ? (
                    <p className="border-l-2 border-primary pl-3 py-1 text-sm italic text-gray-600">
                      Teacher Note: {progress.comments}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-gray-900">Report Cards</h2>
          <div className="space-y-3">
            {currentStudent.reportCards.map((reportCard) => (
              <div
                key={reportCard.id}
                className="group flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-white p-3 transition-all hover:border-primary/30 hover:shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-blue-50 p-2 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                    <FileText size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{reportCard.title}</p>
                    <p className="text-xs text-gray-500">Issued {reportCard.date}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-primary hover:text-primary"
                  onClick={() => window.open('#', '_blank')}
                >
                  View &amp; Print
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export function ParentAcademicPerformanceSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<ParentAcademicPerformanceSourceFaithfulPage />} />
      <Route path=":studentId" element={<ParentAcademicPerformanceSourceFaithfulPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
